<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import MessageFormatter from 'shared/helpers/MessageFormatter';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { usePolicy } from 'dashboard/composables/usePolicy';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import InternalChatChannelList from './InternalChatChannelList.vue';
import InternalChatConversation from './InternalChatConversation.vue';
import InternalChatNotifications from './InternalChatNotifications.vue';
import InternalChatThread from './InternalChatThread.vue';
import InternalChatChannelSettings from './InternalChatChannelSettings.vue';
import InternalChatCreateDialog from './InternalChatCreateDialog.vue';
import { useInternalChatChannelManagement } from './useInternalChatChannelManagement';
import { useInternalChatSend } from './useInternalChatSend';
import { useInternalChatDrafts } from './useInternalChatDrafts';

const createDebounced = (callback, delay) => {
  let timer;

  const debounced = (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      timer = undefined;
      callback(...args);
    }, delay);
  };

  debounced.cancel = () => {
    clearTimeout(timer);
    timer = undefined;
  };

  return debounced;
};

const store = useStore();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const accountId = computed(() => store.getters.getCurrentAccountId);
const threadState = computed(
  () => store.getters['internalChat/getThreadState']
);
const { t } = useI18n();
const { checkPermissions } = usePolicy();
const currentUser = useMapGetter('getCurrentUser');
const canAccess = computed(
  () =>
    currentUser.value.accounts?.some(
      account =>
        Number(account.id) === Number(accountId.value) &&
        account.internal_chat_enabled === true
    ) && checkPermissions(['administrator', 'internal_chat_access'])
);
const agents = useMapGetter('agents/getVerifiedAgents');
const channels = computed(() => store.getters['internalChat/getChannels']);
const categories = computed(() => store.getters['internalChat/getCategories']);
const selectedChannel = computed(
  () => store.getters['internalChat/getSelectedChannel']
);
const selectedChannelId = computed(
  () => store.getters['internalChat/getSelectedChannelId']
);
const messages = computed(() =>
  store.getters['internalChat/getMessages'](selectedChannelId.value)
);
const messageMeta = computed(() =>
  selectedChannelId.value
    ? store.getters['internalChat/getMessageMeta'](selectedChannelId.value)
    : {}
);
const channelMembers = computed(() =>
  selectedChannelId.value
    ? store.getters['internalChat/getMembers'](selectedChannelId.value)
    : { users: [], teams: [] }
);
const thread = computed(() => store.getters['internalChat/getThread']);
const navigationTarget = computed(
  () => store.getters['internalChat/getNavigationTarget']
);
const rootDraft = computed(() =>
  selectedChannelId.value
    ? store.getters['internalChat/getDraft'](selectedChannelId.value)
    : null
);
const threadDraft = computed(() =>
  selectedChannelId.value && thread.value?.parent?.id
    ? store.getters['internalChat/getDraft'](
        selectedChannelId.value,
        thread.value.parent.id
      )
    : null
);
const notifications = computed(
  () => store.getters['internalChat/getNotifications']
);
const unreadNotifications = computed(
  () => store.getters['internalChat/getUnreadNotifications']
);
const searchResults = computed(
  () => store.getters['internalChat/getSearchResults']
);
const uiFlags = computed(() => store.getters['internalChat/getUIFlags']);
const errors = computed(() => store.getters['internalChat/getErrors']);
const messageActionError = computed(() =>
  errors.value.messageAction ? t('INTERNAL_CHAT.ACTION_FAILED') : ''
);
const typingUsers = computed(() =>
  selectedChannelId.value
    ? store.getters['internalChat/getTypingUsers'](selectedChannelId.value)
    : []
);

const showNotifications = ref(false);
const searchQuery = ref('');
const conversation = ref(null);
const threadPanel = ref(null);
const deleteMessageDialog = ref(null);
const messageToDelete = ref(null);
const isDeletingMessage = ref(false);
const deleteMessageError = ref('');

const drafts = useInternalChatDrafts({
  store,
  accountId,
  channelId: selectedChannelId,
  parentId: computed(() => thread.value?.parent?.id),
});
const canCreatePublic = computed(() =>
  checkPermissions(['administrator', 'internal_chat_public_channel_create'])
);
const canCreatePrivate = computed(() =>
  checkPermissions(['administrator', 'internal_chat_private_channel_create'])
);
const canCreateDm = computed(() =>
  checkPermissions(['administrator', 'internal_chat_dm_create'])
);
const availableAgents = computed(() =>
  agents.value.filter(agent => agent.id !== currentUser.value.id)
);
const dmGroupRecipients = computed(() => {
  const available = new Set(availableAgents.value.map(agent => agent.id));
  return channelMembers.value.users
    .map(member => member.user.id)
    .filter(id => available.has(id));
});
const currentMembership = computed(() =>
  channelMembers.value.users.find(
    member => member.user?.id === currentUser.value?.id
  )
);
const canManageMessages = computed(
  () =>
    checkPermissions(['administrator']) ||
    currentMembership.value?.role === 'admin'
);
const canManageChannel = computed(() => canManageMessages.value);
const canManageCategories = computed(() => checkPermissions(['administrator']));
const canMentionAll = computed(
  () => canManageMessages.value && Boolean(selectedChannelId.value)
);

const runSearch = createDebounced(async query => {
  try {
    await store.dispatch('internalChat/search', query);
  } catch {
    // The store exposes only the current query error; obsolete requests are ignored.
  }
}, 300);

watch(searchQuery, value => {
  store.dispatch('internalChat/clearSearch');
  runSearch(value);
});

const selectFirstChannel = list => {
  if (!selectedChannelId.value && list.length)
    store.dispatch('internalChat/selectChannel', list[0].id);
};

let notificationSelection = 0;
const selectChannel = channelId => {
  notificationSelection += 1;
  showNotifications.value = false;
  store.dispatch('internalChat/selectChannel', channelId);
};

const {
  createTarget,
  settingsTarget,
  openCreate,
  openSettings,
  close: closeManagement,
  perform: performChannelAction,
} = useInternalChatChannelManagement({
  store,
  accountId,
  selectedChannel,
  canManageChannel,
  canCreate: computed(() => ({
    public: canCreatePublic.value,
    private: canCreatePrivate.value,
    dm: canCreateDm.value,
  })),
  t,
});

const {
  isSending: sendingMessage,
  failed: sendFailed,
  send: sendMessageToChannel,
} = useInternalChatSend({
  store,
  accountId,
  channelId: selectedChannelId,
  threadState,
  conversation,
  threadPanel,
  beforeSend: async () => {
    await drafts.flush();
  },
});
const isSending = computed(
  () => sendingMessage.value || uiFlags.value.isSending
);
const sendMessage = payload => sendMessageToChannel(payload);

const openThread = async message => {
  try {
    await store.dispatch('internalChat/fetchThread', {
      channelId: selectedChannelId.value,
      messageId: message.id,
      parent: message,
    });
  } catch {
    // The thread retains its original and exposes the request error for retry.
  }
};

const sendThreadMessage = async payload => {
  if (!thread.value?.parent?.id) return;
  await sendMessageToChannel(payload, thread.value.parent.id);
};

const sendTypingOn = createDebounced(channelId => {
  store.dispatch('internalChat/setTyping', { channelId, state: 'on' });
}, 250);

const setTyping = state => {
  if (!selectedChannelId.value) return;
  if (state) {
    sendTypingOn(selectedChannelId.value);
  } else {
    sendTypingOn.cancel();
    store.dispatch('internalChat/setTyping', {
      channelId: selectedChannelId.value,
      state: 'off',
    });
  }
};

const draftChanged = drafts.change;

watch(
  [accountId, canAccess],
  async ([id, allowed]) => {
    drafts.cancel();
    runSearch.cancel();
    sendTypingOn.cancel();
    store.dispatch('internalChat/reset');
    if (!allowed) {
      router.replace(accountScopedRoute('home'));
      return;
    }
    await Promise.all([
      store.dispatch('internalChat/initialize'),
      store.dispatch('agents/get'),
    ]);
    if (accountId.value === id && canAccess.value)
      selectFirstChannel(channels.value);
  },
  { immediate: true }
);
onBeforeUnmount(() => {
  drafts.cancel();
  store.dispatch('internalChat/reset');
});
onBeforeUnmount(() => runSearch.cancel());
onBeforeUnmount(() => sendTypingOn.cancel());

const searchPreview = message => {
  if (message.content_attributes?.content_format !== 'markdown')
    return message.content;
  const formatter = new MessageFormatter(message.content);
  formatter.disableImageRendering();
  return formatter.plainText;
};

const openMessage = async (channelId, messageId) => {
  notificationSelection += 1;
  const selection = notificationSelection;
  const account = accountId.value;
  const generation = store.state.internalChat.contextGeneration;
  const current = () =>
    selection === notificationSelection &&
    account === accountId.value &&
    generation === store.state.internalChat.contextGeneration &&
    canAccess.value &&
    selectedChannelId.value === channelId;
  showNotifications.value = false;
  try {
    const target = await store.dispatch('internalChat/openMessage', {
      channelId,
      messageId,
    });
    if (!target || !current()) return;
    await nextTick();
    if (!current()) return;
    const panel = target.parent_id ? threadPanel : conversation;
    panel.value?.focusMessage(target.id);
  } catch {
    // The store exposes the current navigation error without resurrecting old context.
  }
};

const openSearchResult = result => {
  searchQuery.value = '';
  openMessage(result.channel_id, result.id);
};

const toggleReaction = ({ messageId, emoji, reactionId, userId }) => {
  if (!selectedChannelId.value) return;
  if (reactionId) {
    store.dispatch('internalChat/deleteReaction', {
      channelId: selectedChannelId.value,
      messageId,
      reactionId,
      emoji,
      userId,
    });
    return;
  }

  store.dispatch('internalChat/createReaction', {
    channelId: selectedChannelId.value,
    messageId,
    emoji,
  });
};

const selectNotification = async notification => {
  const account = accountId.value;
  const generation = store.state.internalChat.contextGeneration;
  const channelSelection = store.state.internalChat.channelSelectionGeneration;
  const threadGeneration = threadState.value.generation;
  notificationSelection += 1;
  const selection = notificationSelection;
  await store.dispatch('internalChat/readNotification', notification.id);
  if (
    account !== accountId.value ||
    generation !== store.state.internalChat.contextGeneration ||
    channelSelection !== store.state.internalChat.channelSelectionGeneration ||
    threadGeneration !== threadState.value.generation ||
    selection !== notificationSelection ||
    !canAccess.value
  )
    return;
  await openMessage(notification.channel_id, notification.message_id);
};

const loadHistory = async action => {
  const channelId = selectedChannelId.value;
  if (!channelId) return;
  const generation = store.state.internalChat.contextGeneration;
  const selection = store.state.internalChat.channelSelectionGeneration;
  const current = () =>
    generation === store.state.internalChat.contextGeneration &&
    selection === store.state.internalChat.channelSelectionGeneration &&
    channelId === selectedChannelId.value;
  try {
    await conversation.value.preserveHistory(
      () => store.dispatch(`internalChat/${action}`, channelId),
      current
    );
  } catch {
    // The store retains the loaded page and cursor for retry.
  }
};

const updateMessage = async ({
  message,
  content,
  contentFormat,
  mentionedUserIds,
  mentionedTeamIds,
  mentionAll,
}) => {
  if (message.account_id !== accountId.value)
    throw new Error('Internal chat edit context changed');
  await store.dispatch('internalChat/updateMessage', {
    channelId: message.channel_id,
    messageId: message.id,
    content,
    contentFormat,
    mentionedUserIds,
    mentionedTeamIds,
    mentionAll,
  });
};

const deleteMessage = message => {
  messageToDelete.value = message;
  deleteMessageError.value = '';
  deleteMessageDialog.value.open();
};

const confirmDeleteMessage = async () => {
  if (isDeletingMessage.value || !messageToDelete.value) return;
  const { channel_id: channelId, id: messageId } = messageToDelete.value;
  isDeletingMessage.value = true;
  deleteMessageError.value = '';
  try {
    await store.dispatch('internalChat/deleteMessage', {
      channelId,
      messageId,
    });
    deleteMessageDialog.value.close();
  } catch {
    deleteMessageError.value = t('INTERNAL_CHAT.ACTION_FAILED');
  } finally {
    isDeletingMessage.value = false;
  }
};
</script>

<template>
  <section
    v-if="canAccess"
    class="relative flex h-full min-w-0 flex-1 overflow-hidden bg-n-surface-1"
  >
    <div
      class="h-full shrink-0"
      :class="selectedChannelId ? 'hidden md:block' : 'block'"
    >
      <InternalChatChannelList
        :channels="channels"
        :categories="categories"
        :selected-channel-id="selectedChannelId"
        :is-loading="uiFlags.isFetchingChannels"
        @select="selectChannel"
      />
    </div>
    <p
      v-if="errors.navigation || errors.messages"
      data-testid="internal-chat-navigation-error"
      role="alert"
      class="m-0 border-b border-n-weak px-4 py-2 text-sm text-n-ruby-11"
    >
      {{ t('INTERNAL_CHAT.NAVIGATION_FAILED') }}
    </p>
    <main class="flex min-w-0 flex-1 flex-col">
      <header
        class="flex h-14 shrink-0 items-center justify-end gap-2 border-b border-n-weak px-4"
      >
        <div class="relative me-auto w-full min-w-0 max-w-sm">
          <label class="sr-only" for="internal-chat-search">
            {{ t('INTERNAL_CHAT.SEARCH_LABEL') }}
          </label>
          <Input
            id="internal-chat-search"
            v-model="searchQuery"
            type="search"
            size="sm"
            custom-input-class="ltr:!pl-8 rtl:!pr-8"
            :placeholder="t('INTERNAL_CHAT.SEARCH_PLACEHOLDER')"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="pointer-events-none absolute top-1/2 size-3.5 -translate-y-1/2 text-n-slate-11 ltr:left-2.5 rtl:right-2.5"
              />
            </template>
          </Input>
          <p
            v-if="errors.search"
            data-testid="internal-chat-search-error"
            role="alert"
            class="absolute inset-x-0 top-11 z-40 rounded-lg bg-n-background p-3 text-sm text-n-ruby-11 shadow-xl outline outline-1 outline-n-weak"
          >
            {{ t('INTERNAL_CHAT.SEARCH_FAILED') }}
          </p>
          <div
            v-if="searchQuery.length >= 2 && searchResults.length"
            class="absolute inset-x-0 top-11 z-40 max-h-72 overflow-y-auto rounded-lg bg-n-background p-1 shadow-xl outline outline-1 outline-n-weak"
          >
            <button
              v-for="result in searchResults"
              :key="result.id"
              type="button"
              class="block w-full rounded-md px-3 py-2 text-start hover:bg-n-alpha-2"
              @click="openSearchResult(result)"
            >
              <span class="block truncate text-sm text-n-slate-12">{{
                searchPreview(result)
              }}</span>
              <span class="block text-xs text-n-slate-10">{{
                result.channel?.name
              }}</span>
            </button>
          </div>
        </div>
        <Button
          v-if="canCreatePublic"
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-plus"
          class="shrink-0 max-xl:w-8 max-xl:px-0 [&>.truncate]:hidden xl:[&>.truncate]:block"
          :label="t('INTERNAL_CHAT.NEW_PUBLIC')"
          :aria-label="t('INTERNAL_CHAT.NEW_PUBLIC')"
          @click="openCreate('public')"
        />
        <Button
          v-if="canCreatePrivate"
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-lock-keyhole"
          class="shrink-0 max-xl:w-8 max-xl:px-0 [&>.truncate]:hidden xl:[&>.truncate]:block"
          :label="t('INTERNAL_CHAT.NEW_PRIVATE')"
          :aria-label="t('INTERNAL_CHAT.NEW_PRIVATE')"
          @click="openCreate('private')"
        />
        <Button
          v-if="canCreateDm"
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-user-round-plus"
          class="shrink-0 max-xl:w-8 max-xl:px-0 [&>.truncate]:hidden xl:[&>.truncate]:block"
          :label="t('INTERNAL_CHAT.NEW_DM')"
          :aria-label="t('INTERNAL_CHAT.NEW_DM')"
          @click="openCreate('dm')"
        />
        <Button
          v-if="canCreateDm && selectedChannel?.channel_type === 'dm'"
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-users-round"
          :label="t('INTERNAL_CHAT.NEW_DM_GROUP')"
          :aria-label="t('INTERNAL_CHAT.NEW_DM_GROUP')"
          data-testid="internal-chat-new-dm-group"
          @click="openCreate('dm', dmGroupRecipients)"
        />
        <Button
          v-if="selectedChannel && canManageChannel"
          type="button"
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-settings-2"
          class="shrink-0"
          :aria-label="t('INTERNAL_CHAT.CHANNEL_SETTINGS')"
          @click="openSettings"
        />
        <div class="relative">
          <Button
            type="button"
            variant="ghost"
            color="slate"
            size="sm"
            class="relative"
            :aria-label="t('INTERNAL_CHAT.NOTIFICATIONS')"
            :aria-expanded="showNotifications"
            @click="showNotifications = !showNotifications"
          >
            <template #icon>
              <Icon icon="i-lucide-bell" />
              <span
                v-if="unreadNotifications"
                class="absolute end-1 top-1 size-2 rounded-full bg-n-brand"
              />
            </template>
          </Button>
          <InternalChatNotifications
            v-if="showNotifications"
            :notifications="notifications"
            :unread-count="unreadNotifications"
            @select="selectNotification"
            @read-all="store.dispatch('internalChat/readAllNotifications')"
          />
        </div>
      </header>
      <div
        v-if="errors.channels"
        class="border-b border-n-ruby-8 bg-n-ruby-2 px-4 py-2 text-sm text-n-ruby-11"
        role="alert"
      >
        {{ t('INTERNAL_CHAT.LOAD_FAILED') }}
      </div>
      <div class="flex min-h-0 flex-1">
        <InternalChatConversation
          ref="conversation"
          class="min-w-0 flex-1"
          :class="selectedChannelId ? 'flex' : 'hidden md:flex'"
          :channel="selectedChannel"
          :messages="messages"
          :current-user-id="currentUser.id"
          :is-loading="
            uiFlags.isFetchingMessages || uiFlags.isNavigatingMessage
          "
          :is-sending="isSending"
          :error="sendFailed(null) ? t('INTERNAL_CHAT.SEND_FAILED') : ''"
          :draft="rootDraft"
          :typing-users="typingUsers"
          :members="channelMembers"
          :can-mention-all="canMentionAll"
          :can-manage-messages="canManageMessages"
          :message-meta="messageMeta"
          :focused-message-id="
            navigationTarget?.root_id ||
            navigationTarget?.parent_id ||
            navigationTarget?.id
          "
          :action-error="messageActionError"
          :save-message="updateMessage"
          @send="sendMessage"
          @open-thread="openThread"
          @react="toggleReaction"
          @draft-change="draftChanged(null, $event)"
          @typing="setTyping"
          @delete="deleteMessage"
          @load-older="loadHistory('loadOlderMessages')"
          @load-newer="loadHistory('loadNewerMessages')"
          @back="store.commit('internalChat/SET_SELECTED_CHANNEL', null)"
        />
        <InternalChatThread
          ref="threadPanel"
          :thread="thread"
          :history-state="threadState"
          :refresh="() => store.dispatch('internalChat/refreshThread')"
          :load-older="() => store.dispatch('internalChat/loadOlderThread')"
          :load-newer="() => store.dispatch('internalChat/loadNewerThread')"
          :focused-message-id="
            navigationTarget?.parent_id ? navigationTarget.id : null
          "
          :current-user-id="currentUser.id"
          :draft="threadDraft"
          :is-sending="isSending"
          :error="
            sendFailed(thread?.parent?.id) ? t('INTERNAL_CHAT.SEND_FAILED') : ''
          "
          :members="channelMembers"
          :can-mention-all="canMentionAll"
          :can-manage-messages="canManageMessages"
          :action-error="messageActionError"
          :save-message="updateMessage"
          @close="store.commit('internalChat/SET_THREAD', null)"
          @send="sendThreadMessage"
          @draft-change="draftChanged(thread?.parent?.id, $event)"
          @typing="setTyping"
          @delete="deleteMessage"
        />
      </div>
    </main>

    <InternalChatCreateDialog
      v-if="createTarget"
      :key="createTarget.generation"
      :mode="createTarget.mode"
      :initial-user-ids="createTarget.initialUserIds"
      :agents="availableAgents"
      :is-loading="createTarget.isLoading"
      :error="createTarget.error"
      :perform-action="performChannelAction"
      @close="closeManagement"
    />
    <Dialog
      ref="deleteMessageDialog"
      type="alert"
      width="md"
      :title="t('INTERNAL_CHAT.DELETE_MESSAGE')"
      :description="t('INTERNAL_CHAT.DELETE_MESSAGE_DESCRIPTION')"
      :confirm-button-label="t('INTERNAL_CHAT.DELETE')"
      :cancel-button-label="t('INTERNAL_CHAT.CANCEL')"
      :is-loading="isDeletingMessage"
      :disable-confirm-button="!messageToDelete"
      :confirm-before-close="isDeletingMessage"
      @confirm="confirmDeleteMessage"
      @close="messageToDelete = null"
    >
      <p
        v-if="deleteMessageError"
        class="mb-0 text-sm text-n-ruby-11"
        role="alert"
      >
        {{ deleteMessageError }}
      </p>
    </Dialog>
    <InternalChatChannelSettings
      v-if="settingsTarget"
      :key="settingsTarget.generation"
      :channel="settingsTarget.channel"
      :categories="categories"
      :members="channelMembers"
      :agents="agents"
      :can-manage-categories="canManageCategories"
      :is-loading="settingsTarget.isLoading"
      :error="settingsTarget.error"
      :perform-action="performChannelAction"
      @close="closeManagement"
    />
  </section>
</template>
